import React, { useState } from "react";
import { Link, NavLink, useNavigate, useLocation } from "react-router-dom";
import { Menu, X, LayoutDashboard, ChevronDown, ShoppingCart } from "lucide-react";
import Logo from "./Logo";
import { ThemeToggle, LanguageToggle } from "./Toggles";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
} from "./ui/dropdown-menu";
import { useI18n } from "@/lib/i18n";
import { useAuth, isStaff } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";

export default function Navbar() {
  const { t } = useI18n();
  const { user, logout } = useAuth();
  const { count } = useCart();
  const [open, setOpen] = useState(false);
  const nav = useNavigate();
  const location = useLocation();

  const primaryLinks = [
    ["/", t("nav.home")],
    ["/services", t("nav.services")],
    ["/packages", t("nav.packages")],
    ["/shop", t("nav.shop")],
    ["/portfolio", t("nav.portfolio")],
  ];
  const moreLinks = [
    ["/how-it-works", t("nav.process")],
    ["/about", t("nav.about")],
    ["/blog", t("nav.blog")],
    ["/contact", t("nav.contact")],
  ];
  const allLinks = [...primaryLinks, ...moreLinks];
  const moreActive = moreLinks.some(([to]) => location.pathname.startsWith(to));

  const navItemCls = ({ isActive }) =>
    `relative px-3 xl:px-3.5 py-2 text-[15px] font-medium rounded-lg whitespace-nowrap transition-colors ${
      isActive ? "text-primary" : "link-muted hover:text-foreground"
    }`;

  return (
    <header className="glass sticky top-0 z-50">
      <div className="mx-auto max-w-[1440px] px-5 sm:px-8 flex h-16 lg:h-[72px] items-center justify-between gap-6">
        <Logo className="shrink-0" />

        {/* Desktop navigation */}
        <nav className="hidden lg:flex items-center gap-0.5 xl:gap-1.5">
          {primaryLinks.map(([to, label]) => (
            <NavLink key={to} to={to} end={to === "/"}
              data-testid={`nav-${to === "/" ? "home" : to.slice(1)}`}
              className={navItemCls}>
              {label}
            </NavLink>
          ))}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                data-testid="nav-more-trigger"
                className={`group inline-flex items-center gap-1 px-3 xl:px-3.5 py-2 text-[15px] font-medium rounded-lg whitespace-nowrap transition-colors outline-none ${
                  moreActive ? "text-primary" : "link-muted hover:text-foreground"
                }`}>
                {t("nav.more")}
                <ChevronDown size={15} className="transition-transform duration-200 group-data-[state=open]:rotate-180" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" sideOffset={10} className="min-w-[13rem] p-1.5">
              {moreLinks.map(([to, label]) => (
                <DropdownMenuItem key={to} asChild>
                  <Link to={to} data-testid={`nav-more-${to.slice(1)}`}
                    className={`w-full cursor-pointer rounded-md px-3 py-2 text-sm font-medium ${
                      location.pathname.startsWith(to) ? "text-primary" : ""
                    }`}>
                    {label}
                  </Link>
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </nav>

        {/* Desktop right controls */}
        <div className="flex items-center gap-2.5">
          <Link to="/shop/cart" className="relative flex h-9 w-9 items-center justify-center rounded-md border border-border hover:border-primary transition-colors" data-testid="nav-cart" aria-label="Cart">
            <ShoppingCart size={17} />
            {count > 0 && <span className="absolute -top-1.5 -right-1.5 h-4 min-w-4 px-1 rounded-full bg-primary text-primary-foreground text-[10px] font-bold flex items-center justify-center" data-testid="nav-cart-count">{count}</span>}
          </Link>
          <div className="hidden lg:flex items-center gap-2">
            <LanguageToggle />
            <ThemeToggle />
          </div>
          <div className="hidden lg:block h-6 w-px bg-border mx-0.5" />
          {user ? (
            <div className="hidden lg:flex items-center gap-2.5">
              <Link to={isStaff(user) ? "/admin" : "/portal"} className="btn-ghost !py-2 !px-4 text-sm whitespace-nowrap" data-testid="nav-dashboard">
                <LayoutDashboard size={16} /> {isStaff(user) ? t("nav.admin") : t("nav.portal")}
              </Link>
              <button onClick={() => { logout(); nav("/"); }} className="link-muted text-sm px-2 whitespace-nowrap" data-testid="nav-logout">
                {t("nav.logout")}
              </button>
            </div>
          ) : (
            <div className="hidden lg:flex items-center gap-3">
              <Link to="/login" className="link-muted text-sm font-medium whitespace-nowrap" data-testid="nav-login">{t("nav.login")}</Link>
              <Link to="/quote" className="btn-primary !py-2.5 !px-5 text-sm whitespace-nowrap" data-testid="nav-quote">{t("nav.quote")}</Link>
            </div>
          )}
          <button onClick={() => setOpen(!open)} className="lg:hidden flex h-9 w-9 items-center justify-center rounded-md border border-border" data-testid="mobile-menu-btn">
            {open ? <X size={18} /> : <Menu size={18} />}
          </button>
        </div>
      </div>

      {/* Mobile menu (unchanged behavior) */}
      {open && (
        <div className="lg:hidden border-t border-border bg-background">
          <div className="container-x py-4 flex flex-col gap-1">
            {allLinks.map(([to, label]) => (
              <Link key={to} to={to} onClick={() => setOpen(false)} className="py-2 link-muted font-medium" data-testid={`mnav-${to === "/" ? "home" : to.slice(1)}`}>{label}</Link>
            ))}
            <div className="flex items-center gap-2 py-2"><LanguageToggle /><ThemeToggle /></div>
            {user ? (
              <>
                <Link to={isStaff(user) ? "/admin" : "/portal"} onClick={() => setOpen(false)} className="btn-ghost mt-2">{isStaff(user) ? t("nav.admin") : t("nav.portal")}</Link>
                <button onClick={() => { logout(); setOpen(false); nav("/"); }} className="link-muted text-left py-2">{t("nav.logout")}</button>
              </>
            ) : (
              <>
                <Link to="/login" onClick={() => setOpen(false)} className="btn-ghost mt-2">{t("nav.login")}</Link>
                <Link to="/quote" onClick={() => setOpen(false)} className="btn-primary mt-2">{t("nav.quote")}</Link>
              </>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
